'use client';

import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { JobUpdateRecord } from '@/lib/types';
import { useMemo, useState } from 'react';
import JobUpdatesList from './JobUpdatesList';
import UpdatesLocationPicker from './UpdatesLocationPicker';

type FilterKey = 'all' | 'india' | 'state' | 'district';

interface JobsFilterPanelProps {
  records: JobUpdateRecord[];
  stateName: string;
  districtName: string;
  stateCode: string;
  districtCode: string;
  stateSlug: string;
  districtSlug: string;
}

export default function JobsFilterPanel({
  records,
  stateName,
  districtName,
  stateCode,
  districtCode,
  stateSlug,
  districtSlug,
}: JobsFilterPanelProps) {
  const [activeFilter, setActiveFilter] = useState<FilterKey>('all');
  const [showPicker, setShowPicker] = useState(false);

  const filteredRecords = useMemo(() => {
    if (activeFilter === 'state') {
      return records.filter(
        (record) =>
          record.stateCode === stateCode &&
          String(record.districtCode || '').toUpperCase() === 'ALL',
      );
    }
    if (activeFilter === 'district') {
      return records.filter(
        (record) =>
          record.stateCode === stateCode &&
          String(record.districtCode || '').toUpperCase() !== 'ALL',
      );
    }
    // "all" and "india" intentionally show complete available list.
    return records;
  }, [activeFilter, records, stateCode, districtCode]);

  const chips: Array<{ key: FilterKey; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'state', label: stateName },
    { key: 'district', label: districtName },
  ];

  return (
    <section className='space-y-3'>
      <div className='flex items-center gap-2 overflow-x-auto no-scrollbar'>
        <button
          type='button'
          onClick={() => setShowPicker((prev) => !prev)}
          className='flex h-9 w-9 items-center justify-center bg-white text-gray-700'
          aria-label='Open location filter'
        >
          <AdjustmentsHorizontalIcon className='h-5 w-5' />
        </button>
        {chips.map((chip) => (
          <button
            key={chip.key}
            type='button'
            onClick={() => setActiveFilter(chip.key)}
            className={`shrink-0 rounded-md border px-4 py-1.5 text-xs font-medium ${
              activeFilter === chip.key
                ? 'border-primary bg-primary text-white'
                : 'border-gray-300 bg-gray-100 text-gray-700'
            }`}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {showPicker ? (
        <UpdatesLocationPicker
          mode='jobs'
          initialStateSlug={stateSlug}
          initialDistrictSlug={districtSlug}
        />
      ) : null}

      <JobUpdatesList records={filteredRecords} />
    </section>
  );
}
